import { getLocale, t, useI18n } from '../../i18n';
import React, { useEffect, useRef, useState } from 'react';
import { FiChevronDown } from 'react-icons/fi';
import type { ClientContractRow } from '../../services/clientService';
import { COUNTRIES, getCountryName } from './countries';

export interface ClientFormValues {
  clientName: string;
  /** Empty lets the database apply the agency's country. */
  countryCode: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  billingEmail: string;
  clientNotes: string;
}

interface ClientModalProps {
  mode: 'create' | 'edit';
  initial?: ClientContractRow | null;
  /** The agency's country, which a new client starts in. */
  defaultCountryCode?: string | null;
  isSubmitting: boolean;
  error: string;
  onSubmit: (values: ClientFormValues) => void;
  onClose: () => void;
}

export default function ClientModal({
  mode,
  initial = null,
  defaultCountryCode = null,
  isSubmitting,
  error,
  onSubmit,
  onClose,
}: ClientModalProps) {
  useI18n();
  const [values, setValues] = useState<ClientFormValues>(() => ({
    clientName: initial?.name ?? '',
    countryCode: initial?.country_code ?? defaultCountryCode ?? '',
    contactName: initial?.contact_name ?? '',
    contactEmail: initial?.contact_email ?? '',
    contactPhone: initial?.contact_phone ?? '',
    billingEmail: initial?.billing_email ?? '',
    clientNotes: initial?.notes ?? '',
  }));
  const [nameError, setNameError] = useState('');

  // Creating a client should read as two fields; the rest can be filled in
  // whenever they are known. Editing opens the block that already has content.
  const [showOptional, setShowOptional] = useState(() => Boolean(
    initial?.contact_name || initial?.contact_phone || initial?.billing_email || initial?.notes,
  ));

  const nameRef = useRef<HTMLInputElement | null>(null);

  // The agency's country can land after the form opens; take it only if the
  // user has not picked one yet.
  useEffect(() => {
    if (!defaultCountryCode) return;
    setValues((current) => (current.countryCode ? current : { ...current, countryCode: defaultCountryCode }));
  }, [defaultCountryCode]);

  // Rebuilt each render so the names follow a change of UI language.
  const collator = new Intl.Collator(getLocale(), { sensitivity: 'base' });
  const countryOptions = COUNTRIES
    .map((country) => ({ code: country.code, name: getCountryName(country.code) ?? country.name }))
    .sort((a, b) => collator.compare(a.name, b.name));

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isSubmitting) onClose();
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [isSubmitting, onClose]);

  const setField = (field: keyof ClientFormValues) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { value } = e.target;
    if (field === 'clientName' && nameError) setNameError('');
    setValues((current) => ({ ...current, [field]: value }));
  };

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && !isSubmitting) onClose();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!values.clientName.trim()) {
      setNameError(t('Client name is required'));
      nameRef.current?.focus();
      return;
    }
    onSubmit(values);
  };

  const submitLabel = mode === 'edit'
    ? (isSubmitting ? t('Saving…') : t('Save Changes'))
    : (isSubmitting ? t('Creating…') : t('Create Client'));

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div
        className="modal client-form"
        role="dialog"
        aria-modal="true"
        aria-labelledby="client-form-title"
      >
        <div className="client-form__head">
          <div>
            <h2 className="modal__title" id="client-form-title">
              {mode === 'edit' ? t('Edit client') : t('New client')}
            </h2>
            <p className="client-form__lead">
              {mode === 'edit'
                ? t('Only the client name is required. Clear a field to remove it.')
                : t('Only the client name is required — you can fill in the rest at any time.')}
            </p>
          </div>
          <button type="button" className="client-form__close" onClick={onClose} aria-label={t('Close')}>
            ✕
          </button>
        </div>

        <div aria-live="polite">
          {error && <div className="msg--error client-form__error">{error}</div>}
        </div>

        <form onSubmit={handleSubmit} noValidate>
          <div className="form__group">
            <label className="form__label" htmlFor="client-form-name">{t('Client name *')}</label>
            <input
              id="client-form-name"
              ref={nameRef}
              className="form__input"
              value={values.clientName}
              onChange={setField('clientName')}
              placeholder={t('Riverside Retail Park')}
              autoFocus
              autoComplete="off"
              aria-invalid={nameError ? true : undefined}
              aria-describedby={nameError ? 'client-form-name-error' : undefined}
            />
            {nameError && (
              <p className="client-form__field-error" id="client-form-name-error">{nameError}</p>
            )}
          </div>

          <div className="form__group">
            <label className="form__label" htmlFor="client-form-country">{t('Country')}</label>
            <select
              id="client-form-country"
              className="form__select"
              value={values.countryCode}
              onChange={setField('countryCode')}
              aria-describedby="client-form-country-hint"
            >
              {!values.countryCode && <option value="">{t('Select a country…')}</option>}
              {countryOptions.map((country) => (
                <option key={country.code} value={country.code}>{country.name}</option>
              ))}
            </select>
            <p className="client-form__hint" id="client-form-country-hint">
              {t('New sites for this client start here, and their address search looks here first.')}
            </p>
          </div>

          <div className="form__group">
            <label className="form__label" htmlFor="client-form-contact-email">{t('Contact email')}</label>
            <input
              id="client-form-contact-email"
              className="form__input"
              type="email"
              inputMode="email"
              spellCheck={false}
              autoComplete="off"
              value={values.contactEmail}
              onChange={setField('contactEmail')}
              placeholder="ops@riversideretail.ie"
              aria-describedby="client-form-contact-email-hint"
            />
            <p className="client-form__hint" id="client-form-contact-email-hint">
              {t('The portal invite is sent here. Leave it blank and add it when you know it.')}
            </p>
          </div>

          <button
            type="button"
            className={`client-form__disclosure${showOptional ? ' client-form__disclosure--open' : ''}`}
            onClick={() => setShowOptional((current) => !current)}
            aria-expanded={showOptional}
            aria-controls="client-form-optional"
          >
            <FiChevronDown aria-hidden="true" />
            {t('Contact, billing and notes')}
            <span className="client-form__disclosure-note">{t('Optional')}</span>
          </button>

          {showOptional && (
            <div className="client-form__optional" id="client-form-optional">
              <div className="client-form__grid">
                <div className="form__group">
                  <label className="form__label" htmlFor="client-form-contact-name">{t('Contact name')}</label>
                  <input
                    id="client-form-contact-name"
                    className="form__input"
                    autoComplete="off"
                    value={values.contactName}
                    onChange={setField('contactName')}
                    placeholder={t('Who you deal with day to day')}
                  />
                </div>
                <div className="form__group">
                  <label className="form__label" htmlFor="client-form-contact-phone">{t('Contact phone')}</label>
                  <input
                    id="client-form-contact-phone"
                    className="form__input"
                    type="tel"
                    inputMode="tel"
                    autoComplete="off"
                    value={values.contactPhone}
                    onChange={setField('contactPhone')}
                    placeholder="+353 1 234 5678"
                  />
                </div>
                <div className="form__group client-form__full">
                  <label className="form__label" htmlFor="client-form-billing-email">{t('Billing email')}</label>
                  <input
                    id="client-form-billing-email"
                    className="form__input"
                    type="email"
                    inputMode="email"
                    spellCheck={false}
                    autoComplete="off"
                    value={values.billingEmail}
                    onChange={setField('billingEmail')}
                    placeholder="accounts@riversideretail.ie"
                    aria-describedby="client-form-billing-email-hint"
                  />
                  <p className="client-form__hint" id="client-form-billing-email-hint">
                    {t('Only needed when invoices go somewhere other than the contact above.')}
                  </p>
                </div>
                <div className="form__group client-form__full">
                  <label className="form__label" htmlFor="client-form-notes">{t('Internal notes')}</label>
                  <textarea
                    id="client-form-notes"
                    className="form__textarea"
                    rows={3}
                    value={values.clientNotes}
                    onChange={setField('clientNotes')}
                    placeholder={t('Access quirks, escalation rules, anything the team should know…')}
                  />
                  <p className="client-form__hint">{t('Never shown to the client.')}</p>
                </div>
              </div>
            </div>
          )}

          <div className="client-form__footer">
            <button
              type="button"
              className="btn btn--secondary"
              onClick={onClose}
              disabled={isSubmitting}
            >
              {t('Cancel')}
            </button>
            <button type="submit" className="btn btn--primary" disabled={isSubmitting}>
              {submitLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
